import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { FaTimes, FaSearch, FaCheckCircle, FaExclamationCircle, FaCheckDouble } from 'react-icons/fa';
import StudentHeader from './StudentHeader';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';
import BulkVerifyModal from './BulkVerifyModal';
import Toast from './Toast';
import useToast from './useToast';

const STATUS_LABEL = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed'
};

const REVIEW_STATUS_LABEL = {
  pending: 'Pending Review',
  approved: 'Approved',
  needs_revision: 'Needs Revision'
};

// Shared review panel for the three "progress module" shapes (AETCOM,
// Pandemic, Foundation Course) — same status enum, same review sub-document
// shape, same /all + /:studentId/:moduleKey/review route pair on the
// backend. Not subject-scoped: any teacher with teachesUG sees every UG
// student's progress here (see server/routes/ugAetcomRoutes.js's /all route
// comment for why).
const ProgressModuleReview = ({ title, apiPath, moduleKeyField, showAttendance, ugBatches }) => {
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewEntry, setReviewEntry] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const { toast, showSuccess, showError } = useToast();

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchProgress = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/all`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setProgress(data.progress || []);
    } catch (err) {
      console.error(`Error fetching UG ${apiPath} progress:`, err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, apiPath]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const openReview = (entry) => {
    setReviewEntry(entry);
    setReviewStatus('');
    setFeedbackText(entry.review?.feedback || '');
  };

  const submitReview = async () => {
    if (!reviewStatus) {
      alert('Please choose Approve or Needs Revision.');
      return;
    }
    try {
      setSubmitting(true);
      const studentId = reviewEntry.studentId?._id || reviewEntry.studentId;
      const moduleKey = reviewEntry[moduleKeyField];
      const res = await fetch(
        `${collegeConfig.api.baseURL}/ug/${apiPath}/${studentId}/${moduleKey}/review`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ status: reviewStatus, feedback: feedbackText })
        }
      );
      const data = await res.json();
      if (data.success) {
        setReviewEntry(null);
        await fetchProgress();
        showSuccess(reviewStatus === 'approved' ? 'Module approved successfully' : 'Module sent back for revision');
      } else {
        showError(data.message || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      showError('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const searchLower = searchTerm.trim().toLowerCase();
  const filtered = progress.filter(p => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (batchFilter) {
      const studentBatchId = p.studentId?.profile?.ugBatchId?._id || p.studentId?.profile?.ugBatchId;
      if (!studentBatchId || studentBatchId.toString() !== batchFilter) return false;
    }
    if (!searchLower) return true;
    const name = `${p.studentId?.profile?.firstName || ''} ${p.studentId?.profile?.lastName || ''}`.toLowerCase();
    return name.includes(searchLower);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, batchFilter, searchTerm]);

  const pageEntries = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const bulkCandidates = filtered.filter(p => (p.review?.status || 'pending') !== 'approved');

  return (
    <div className="ugtr-root">
      <Toast toast={toast} />
      <div className="ugtr-header">
        <div>
          <div className="ugtr-header-title">{title}</div>
          <div className="ugtr-header-sub">{filtered.length} entries</div>
        </div>
        <button
          className="ugtr-bulk-btn"
          onClick={() => setShowBulkModal(true)}
          disabled={bulkCandidates.length === 0}
        >
          <FaCheckDouble /> Bulk Verify
        </button>
      </div>

      <div className="ugtr-filters">
        <div className="ugtr-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by student name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}>
          <option value="">All batches</option>
          {(ugBatches || []).map(b => (
            <option key={b._id} value={b._id}>{b.batchLabel}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="ugtr-empty">Loading entries...</div>
      ) : filtered.length === 0 ? (
        <div className="ugtr-empty">No entries match these filters.</div>
      ) : (
        <>
          <div className="ugtr-list">
            {pageEntries.map(p => (
              <div key={p._id} className="ugtr-entry-card">
                <StudentHeader
                  student={p.studentId}
                  ugBatches={ugBatches}
                  statusBadge={(
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span className={`ugtr-status-badge ugtr-status-${p.status}`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                      <span className={`ugtr-status-badge ugtr-status-${p.review?.status || 'pending'}`}>
                        {REVIEW_STATUS_LABEL[p.review?.status] || REVIEW_STATUS_LABEL.pending}
                      </span>
                    </div>
                  )}
                />

                <div className="ugtr-entry-body">
                  <div className="ugtr-info-item">
                    <span className="ugtr-info-label">Module</span>
                    <span className="ugtr-info-value">{p.moduleTitle}</span>
                  </div>
                  {showAttendance && p.attendancePercent != null && (
                    <div className="ugtr-info-item">
                      <span className="ugtr-info-label">Attendance</span>
                      <span className="ugtr-info-value">{p.attendancePercent}%</span>
                    </div>
                  )}
                  {p.activitiesCompleted && (
                    <div className="ugtr-info-item ugtr-entry-body-full">
                      <span className="ugtr-info-label">Activities Completed</span>
                      <span className="ugtr-info-value">{p.activitiesCompleted}</span>
                    </div>
                  )}
                  {p.reflectionNotes && (
                    <div className="ugtr-info-item ugtr-entry-body-full">
                      <span className="ugtr-info-label">Reflection Notes</span>
                      <span className="ugtr-info-value">{p.reflectionNotes}</span>
                    </div>
                  )}
                  {p.review?.feedback && (
                    <div className="ugtr-info-item ugtr-entry-body-full">
                      <span className="ugtr-info-label">Previous Feedback ({p.review.reviewerName})</span>
                      <span className="ugtr-info-value">{p.review.feedback}</span>
                    </div>
                  )}
                </div>

                <div className="ugtr-entry-actions">
                  <button className="ugtr-review-btn" onClick={() => openReview(p)}>
                    Review Entry
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination currentPage={currentPage} totalCount={filtered.length} onPageChange={setCurrentPage} />
        </>
      )}

      {reviewEntry && (
        <div className="ugtr-modal-overlay" onClick={() => !submitting && setReviewEntry(null)}>
          <div className="ugtr-modal" onClick={e => e.stopPropagation()}>
            <div className="ugtr-modal-head">
              <h3>{reviewEntry.moduleTitle}</h3>
              <FaTimes onClick={() => !submitting && setReviewEntry(null)} />
            </div>

            <div className="ugtr-decision-row">
              <button
                className={`ugtr-decision-btn ugtr-approve ${reviewStatus === 'approved' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('approved')}
              >
                <FaCheckCircle /> Approve
              </button>
              <button
                className={`ugtr-decision-btn ugtr-revision ${reviewStatus === 'needs_revision' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('needs_revision')}
              >
                <FaExclamationCircle /> Needs Revision
              </button>
            </div>

            <label className="ugtr-form-label">Faculty feedback</label>
            <textarea
              className="ugtr-form-textarea"
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              placeholder="Notes for the student..."
            />

            <div className="ugtr-modal-actions">
              <button className="ugtr-btn-secondary" onClick={() => setReviewEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ugtr-btn-primary" onClick={submitReview} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <BulkVerifyModal
          title={`Bulk Verify ${title}`}
          items={bulkCandidates}
          getId={p => p._id}
          renderItemLabel={p => (
            <>
              <strong>{p.studentId?.profile?.firstName} {p.studentId?.profile?.lastName}</strong>
              <span className="ugtr-bulk-item-meta">{p.moduleTitle}</span>
            </>
          )}
          renderForm={(formState, setFormState) => (
            <>
              <div className="ugtr-decision-row">
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-approve ${formState.status === 'approved' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'approved' })}
                >
                  <FaCheckCircle /> Approve
                </button>
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-revision ${formState.status === 'needs_revision' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'needs_revision' })}
                >
                  <FaExclamationCircle /> Needs Revision
                </button>
              </div>
              <label className="ugtr-form-label">Faculty feedback</label>
              <textarea
                className="ugtr-form-textarea"
                value={formState.feedback || ''}
                onChange={e => setFormState({ ...formState, feedback: e.target.value })}
                placeholder="Notes for the students..."
              />
            </>
          )}
          isValid={formState => !!formState.status}
          onConfirm={async (selectedItems, formState) => {
            const results = await Promise.allSettled(
              selectedItems.map(p => {
                const studentId = p.studentId?._id || p.studentId;
                const moduleKey = p[moduleKeyField];
                return fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/${studentId}/${moduleKey}/review`, {
                  method: 'PUT',
                  headers: authHeaders(),
                  body: JSON.stringify({ status: formState.status, feedback: formState.feedback })
                });
              })
            );
            const successCount = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
            setShowBulkModal(false);
            await fetchProgress();
            if (successCount === selectedItems.length) {
              showSuccess(`Successfully verified ${successCount} entries`);
            } else {
              showError(`Verified ${successCount} of ${selectedItems.length} entries. Some failed — please retry those.`);
            }
          }}
          onClose={() => setShowBulkModal(false)}
        />
      )}
    </div>
  );
};

export default ProgressModuleReview;
